import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import { FaMinus, FaPlus } from "react-icons/fa";
import FloatingPanel from "../FloatingPanel";
import {
  formatScoreValue,
  hasScore,
  toScoreValue,
} from "../../utils/scoreState";
import {
  getScaleValues,
  getScorePillProps,
} from "../../utils/scoringScale";

// How a score is drawn comes from the scale its heading is rated on: the
// columns, the pill colours and what counts as a value at all.
const getScorePillStyle = (score, scale) => {
  const n = toScoreValue(score, scale);
  if (n === null) return { className: "head_light", style: undefined };
  return getScorePillProps(scale, n, "head_light");
};

// N/A is displayed exactly like an unscored item: blank. So the only thing ever
// rendered for a score is a value the heading's scale defines.
const normalizeScore = (score, scale) => formatScoreValue(score, "", scale);

const getNumericScore = (score, scale) => toScoreValue(score, scale);

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getScoreDate = (score) =>
  score?.assessmentDate || score?.signedAt || score?.createdAt || null;

// Current-version real scores for a subheading. N/A, unanswered records, and
// historical amended versions are skipped.
const getCurrentScores = (scores = [], scale) =>
  scores
    .filter(
    (score) => score?.currentVersion !== false && hasScore(score, scale)
    )
    .sort((a, b) => {
      const aDate = new Date(getScoreDate(a) || 0);
      const bDate = new Date(getScoreDate(b) || 0);
      return bDate - aDate;
    });

const LatestFunctionalScoresOverlay = ({
  isOpen,
  onClose,
  headings = [],
  isLoading,
  error,
  startLabel,
  endLabel,
}) => {
  const [expandedAccordionIds, setExpandedAccordionIds] = useState([]);
  const [activeSubheadings, setActiveSubheadings] = useState([]);

  useEffect(() => {
    if (!isOpen) {
      setExpandedAccordionIds([]);
      setActiveSubheadings([]);
    }
  }, [isOpen]);

  const filteredHeadings = useMemo(() => {
    if (!Array.isArray(headings)) return [];
    return headings
      .map((heading) => ({
        ...heading,
        subheadings: (heading?.subheadings || [])
          .map((subheading) => ({
            ...subheading,
            currentScores: getCurrentScores(
              subheading?.collaboratorScores || [],
              heading?.scoringScale
            ),
          }))
          .filter((subheading) => subheading.currentScores.length > 0),
      }))
      .filter((heading) => heading.subheadings.length > 0);
  }, [headings]);

  useEffect(() => {
    if (!isOpen || filteredHeadings.length === 0) return;

    const firstHeading = filteredHeadings[0];
    const firstHeadingKey = `latest-score-${firstHeading.id || 0}`;
    const firstSubheadingKeys = (firstHeading.subheadings || []).map(
      (subheading, subIndex) => `${firstHeading.id || 0}-${subheading.id || subIndex}`
    );

    setExpandedAccordionIds([firstHeadingKey]);
    setActiveSubheadings(firstSubheadingKeys);
  }, [filteredHeadings, isOpen]);

  const getHeadingSubheadingKeys = (heading, headingIndex) =>
    (heading.subheadings || []).map(
      (subheading, subIndex) =>
        `${heading.id || headingIndex}-${subheading.id || subIndex}`
    );

  const handleAccordionChange = (expandedIds) => {
    const expandedList = Array.isArray(expandedIds) ? expandedIds : [];
    const newlyExpandedIds = expandedList.filter(
      (expandedId) => !expandedAccordionIds.includes(expandedId)
    );
    const collapsedIds = expandedAccordionIds.filter(
      (expandedId) => !expandedList.includes(expandedId)
    );
    const newlyExpandedSubheadingKeys = filteredHeadings.flatMap(
      (heading, headingIndex) => {
        const headingId = `latest-score-${heading.id || headingIndex}`;
        return newlyExpandedIds.includes(headingId)
          ? getHeadingSubheadingKeys(heading, headingIndex)
          : [];
      }
    );
    const collapsedSubheadingKeys = filteredHeadings.flatMap(
      (heading, headingIndex) => {
        const headingId = `latest-score-${heading.id || headingIndex}`;
        return collapsedIds.includes(headingId)
          ? getHeadingSubheadingKeys(heading, headingIndex)
          : [];
      }
    );

    setExpandedAccordionIds(expandedList);
    setActiveSubheadings((current) => {
      const withoutCollapsed = current.filter(
        (subheadingKey) => !collapsedSubheadingKeys.includes(subheadingKey)
      );
      return Array.from(
        new Set([...withoutCollapsed, ...newlyExpandedSubheadingKeys])
      );
    });
  };

  const toggleSubheading = (subheadingKey) => {
    setActiveSubheadings((current) =>
      current.includes(subheadingKey)
        ? current.filter((item) => item !== subheadingKey)
        : [...current, subheadingKey]
    );
  };

  return (
    <FloatingPanel
      isOpen={isOpen}
      onClose={onClose}
      title="Functional Scores"
      subtitle={
        startLabel && endLabel
          ? `Time period: ${startLabel} to ${endLabel}`
          : null
      }
      width={900}
      height={520}
    >
      {isLoading && (
        <div className="text-center py-4">
          <p className="mb-0">Loading functional scores...</p>
        </div>
      )}
      {error && (
        <div className="text-center py-4 text-danger">
          <p className="mb-0">
            Error loading functional scores. Please try again.
          </p>
        </div>
      )}
      {!isLoading && !error && filteredHeadings.length === 0 && (
        <div className="text-center py-4 text-muted">
          <p className="mb-0">No functional scores available.</p>
        </div>
      )}
      {!isLoading && !error && filteredHeadings.length > 0 && (
        <Accordion
          key={
            isOpen
              ? `latest-score-open-${filteredHeadings[0]?.id || 0}`
              : "latest-score-closed"
          }
          allowZeroExpanded
          allowMultipleExpanded
          className="mb-0"
          preExpanded={[`latest-score-${filteredHeadings[0]?.id || 0}`]}
          onChange={handleAccordionChange}
        >
          {filteredHeadings.map((heading, headingIndex) => {
            return (
              <div key={heading.id || headingIndex}>
                <div className="chevron" />
                <AccordionItem
                  className="mb-2"
                  uuid={`latest-score-${heading.id || headingIndex}`}
                >
                  <AccordionItemHeading className="accordion__primary">
                    <AccordionItemButton
                      style={{
                        height: 45,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <small>{heading.headingName}</small>
                    </AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel>
                    <div className="d-flex justify-content-between text-center text-white head_light mx-0 h-27 small-text">
                      <div className="flex-grow-1" style={{ width: 170 }}>
                        <small className="d-flex justify-content-center align-items-center h-100">
                          Functional Issue
                        </small>
                      </div>
                      <div
                        className="border-left-1 d-flex align-items-center justify-content-center"
                        style={{ width: 84 }}
                      >
                        <small className="d-flex justify-content-center align-items-center h-100">
                          Position
                        </small>
                      </div>
                      <div className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1" style={{ width: 109 }}>
                        <small className="d-flex justify-content-center align-items-center h-100">Provider</small>
                      </div>
                      <div className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1" style={{ width: 119 }}>
                        <small className="d-flex justify-content-center align-items-center h-100">Assessment Date</small>
                      </div>
                      {/* One column per value this heading's scale defines. */}
                      {getScaleValues(heading.scoringScale).map((score) => (
                        <div key={score} className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1" style={{ width: 17 }}>
                          <small className="d-flex justify-content-center align-items-center h-100">{score}</small>
                        </div>
                      ))}
                      <div className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1" style={{ width: 180 }}>
                        <small className="d-flex justify-content-center align-items-center h-100">Comments</small>
                      </div>
                      <div className="border-left-1 d-flex align-items-center justify-content-center" style={{ width: 59, flexShrink: 0 }}>
                        <small className="d-flex justify-content-center align-items-center h-100">Median</small>
                      </div>
                      <div className="border-left-1 d-flex align-items-center justify-content-center" style={{ width: 67, flexShrink: 0 }}>
                        <small className="d-flex justify-content-center align-items-center h-100">Team Score</small>
                      </div>
                    </div>

                    {heading.subheadings.map((sub, subIndex) => {
                      const subheadingKey = `${heading.id || headingIndex}-${sub.id || subIndex}`;
                      const isActive = activeSubheadings.includes(subheadingKey);
                      // Display values are blank for anything off the 1-5
                      // scale, so presence is decided on the raw numbers
                      // rather than on what is rendered.
                      const scale = heading.scoringScale;
                      const medianValue = normalizeScore(sub.median, scale);
                      const teamScoreValue = normalizeScore(sub.teamScore, scale);
                      const medianNumber = getNumericScore(sub.median, scale);
                      const teamScoreNumber = getNumericScore(sub.teamScore, scale);
                      const hasTeamScore = teamScoreNumber !== null;
                      const showTeamScore = hasTeamScore && teamScoreNumber !== medianNumber;
                      const medianPill = showTeamScore
                        ? { className: "bg_grey", style: undefined }
                        : getScorePillStyle(medianValue, scale);
                      const teamScorePill = getScorePillStyle(teamScoreValue, scale);

                      return (
                        <Fragment key={sub.id || subIndex}>
                          <div className="d-flex bg_light my-1 h-35 mx-0">
                            <div
                              className="d-flex align-items-center flex-grow-1"
                              style={{
                                minWidth: 170,
                                paddingLeft: "12px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <button
                                type="button"
                                className="decoration-none border-0 bg-transparent p-0 text-left"
                                onClick={() => toggleSubheading(subheadingKey)}
                              >
                                {isActive ? <FaMinus size={11} /> : <FaPlus size={11} />}
                                &nbsp;&nbsp;
                                <small className="small-text">
                                  {sub.subheadingName}
                                </small>
                              </button>
                            </div>
                            <div className="d-flex align-items-center justify-content-center flex-grow-1" style={{ width: 84 }} />
                            <div className="d-flex align-items-center justify-content-center flex-grow-1" style={{ width: 109 }} />
                            <div className="d-flex align-items-center justify-content-center flex-grow-1" style={{ width: 119 }} />
                            {getScaleValues(scale).map((score) => (
                              <div key={score} className="d-flex align-items-center justify-content-center flex-grow-1" style={{ width: 17 }} />
                            ))}
                            <div className="d-flex align-items-center justify-content-center flex-grow-1" style={{ width: 180 }} />
                            <div className="d-flex align-items-center justify-content-center" style={{ width: 59, flexShrink: 0 }}>
                              <div className={`w-35 h-27 rounded-pill d-flex align-items-center justify-content-center ${medianPill.className}`} style={medianPill.style}>
                                <span className="fs-14 font-weight-bold">{medianValue}</span>
                              </div>
                            </div>
                            <div className="d-flex align-items-center justify-content-center" style={{ width: 67, flexShrink: 0 }}>
                              {showTeamScore && (
                                <div className={`w-35 h-27 rounded-pill d-flex align-items-center justify-content-center ${teamScorePill.className}`} style={teamScorePill.style}>
                                  <span className="fs-14 font-weight-bold">{teamScoreValue}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {isActive &&
                            sub.currentScores.map((scoreRow, scoreIndex) => {
                              const scoreNum = getNumericScore(
                                scoreRow?.score,
                                scale
                              );

                              return (
                                <div
                                  key={scoreRow?.id || scoreIndex}
                                  className="d-flex justify-content-between mx-0 h-31 border-bottom-2"
                                  style={{
                                    backgroundColor:
                                      scoreIndex % 2 === 0
                                        ? "#F1F1F1"
                                        : "#F8F8F8",
                                  }}
                                >
                                  <div
                                    className="border-left-2 py-3 bg-white d-flex align-items-center justify-content-center flex-grow-1"
                                    style={{ width: 170 }}
                                  />
                                  <div
                                    className="border-left-2 py-3 d-flex align-items-center fs-10 justify-content-center flex-grow-1"
                                    style={{ width: 84 }}
                                  >
                                    {scoreRow?.position?.abbreviation ||
                                      "N/A"}
                                  </div>
                                  <div
                                    className="border-left-2 py-3 fs-10 d-flex align-items-center justify-content-center flex-grow-1"
                                    style={{ width: 109 }}
                                  >
                                    <span>{`${scoreRow?.lastname || ""}, ${
                                      scoreRow?.firstname || ""
                                    }`}</span>
                                  </div>
                                  <div
                                    className="border-left-2 py-3 fs-10 d-flex align-items-center justify-content-center flex-grow-1"
                                    style={{ width: 119 }}
                                  >
                                    {formatDate(
                                      scoreRow?.assessmentDate
                                    )}
                                  </div>
                                  {getScaleValues(scale).map((score) => {
                                    const pill = getScorePillStyle(
                                      scoreNum,
                                      scale
                                    );
                                    return (
                                      <div
                                        key={score}
                                        className="border-left-2 d-flex align-items-center justify-content-center flex-grow-1"
                                        style={{ width: 17 }}
                                      >
                                        {scoreNum === score && (
                                          <div
                                            className={`w-17 h-17 rounded-circle ${pill.className} d-flex align-items-center justify-content-center`}
                                            style={pill.style}
                                          >
                                            <span className="fs-10 font-weight-bold">
                                              {score}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                  <div
                                    className="border-left-2 pl-1 flex-grow-1 fs-10"
                                    style={{ width: 180 }}
                                  >
                                    {scoreRow?.comment || "N/A"}
                                  </div>
                                  <div
                                    className="border-left-2 d-flex align-items-center justify-content-center fs-10"
                                    style={{ width: 59, flexShrink: 0 }}
                                  />
                                  <div
                                    className="border-left-2 d-flex align-items-center justify-content-center fs-10"
                                    style={{ width: 67, flexShrink: 0 }}
                                  />
                                </div>
                              );
                            })}
                        </Fragment>
                      );
                    })}
                  </AccordionItemPanel>
                </AccordionItem>
              </div>
            );
          })}
        </Accordion>
      )}
    </FloatingPanel>
  );
};

export default LatestFunctionalScoresOverlay;
